import React, { SetStateAction, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { toast } from "sonner";
import { BackLinkWithText } from "../../components/BackLink";
import { CancelIcon } from "../../components/CancelIcon";
import { Card } from "../../components/Card";
import { Chevron } from "../../components/Chevron";
import { CTA } from "../../components/CTA";
import DetailList from "../../components/DetailList";
import { Dropdown } from "../../components/Dropdown";
import { Flex } from "../../components/Flex";
import { H2 } from "../../components/Heading";
import { Input } from "../../components/Input";
import Loading from "../../components/Loading";
import { InlineAddBtn, InlineBtn } from "../../components/NewButton";
import { Text } from "../../components/Text";
import { TextArea } from "../../components/TextArea";
import { View } from "../../components/View";
import { MOBILE_BREAKPOINT } from "../../config";
import {
  ExcludedHoursInput,
  OpeningHoursInput,
  Place,
  PlaceSearchResult,
  useCreateLocationMutation,
  usePlaceQuery,
  useSearchPlacesQuery,
} from "../../graphql/generated";
import useAnalytics from "../../hooks/useAnalytics";
import useGqlClient from "../../hooks/useGqlClient";
import { authSelectors } from "../../store/auth/selector";
import styled from "../../styles";
import { ExcludedHours } from "./Location";

export const daysSortMap: { [key: string]: number } = {
  Monday: 0,
  Tuesday: 1,
  Wednesday: 2,
  Thursday: 3,
  Friday: 4,
  Saturday: 5,
  Sunday: 6,
};

const PageWrap = styled.div`
  flex: 1;
`;

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;

  max-width: 500px;
  align-self: center;
  text-align: left;
`;

const SearchResultsWrap = styled.div`
  border-radius: ${(p) => p.theme.misc.borderRadius};
  background-color: ${(p) => p.theme.color.card.background};
  margin-top: ${(p) => p.theme.spacing.s};
`;

const SearchResultItem = styled.div`
  padding: ${(p) => p.theme.spacing.s} ${(p) => p.theme.spacing.s};
  border-bottom: 1px solid ${(p) => p.theme.color.card.divider};
  &:hover {
    background-color: ${(p) => p.theme.color.card.divider};
    cursor: pointer;
  }
  &:last-of-type {
    padding-bottom: ${(p) => p.theme.spacing.s};
    border-radius: 0 0 ${(p) => p.theme.misc.borderRadius}
      ${(p) => p.theme.misc.borderRadius};
    border-bottom: none;
  }
  &:first-of-type {
    border-radius: ${(p) => p.theme.misc.borderRadius}
      ${(p) => p.theme.misc.borderRadius} 0 0;
    padding-top: ${(p) => p.theme.spacing.s};
  }
`;

const OptionsToggleWrap = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
`;

let searchPlacesTimeout: NodeJS.Timeout | null = null;

export function CreateLocation() {
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState<PlaceSearchResult[]>([]);
  const [selectedPlaceId, setSelectedPlaceId] = useState("");

  const client = useGqlClient();
  const { isLoading, error, refetch } = useSearchPlacesQuery(
    client,
    {
      input: searchTerm,
    },
    {
      enabled: false,
    }
  );

  return (
    <Wrap>
      <PageWrap>
        <BackLinkWithText to="/b/locations" text="Back to locations" />
        <Card padding="l" margin="xl 0 xl" style={{ overflow: "visible" }}>
          <H2 margin="0 0 xl">Create new location</H2>

          {selectedPlaceId ? (
            <PlaceForm
              placeId={selectedPlaceId}
              setSelectedPlaceId={setSelectedPlaceId}
              setSearchTerm={setSearchTerm}
            ></PlaceForm>
          ) : (
            <>
              <Input
                label="Search for address"
                placeholder="e.g. Honest Burgers Borough"
                value={searchTerm}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setSelectedPlaceId("");
                  if (e.currentTarget.value === searchTerm) {
                    return;
                  }

                  if (e.currentTarget.value.length <= 2) {
                    setSearchResults([]);
                    setSearchTerm(e.currentTarget.value);
                    return;
                  }

                  if (searchPlacesTimeout) {
                    clearTimeout(searchPlacesTimeout);
                  }

                  setSearchTerm(e.currentTarget.value);

                  searchPlacesTimeout = setTimeout(async () => {
                    if (searchTerm === "") {
                      return;
                    }

                    const res = await refetch();
                    if (res.data) {
                      setSearchResults(res.data.searchPlaces.places);
                    }
                  }, 1000);
                }}
              />
              <SearchResults
                isLoading={isLoading}
                results={searchResults}
                error={error}
                onPlaceSelected={(place) => {
                  setSearchResults([]);
                  setSelectedPlaceId(place.placeId);
                }}
              />
            </>
          )}
        </Card>
      </PageWrap>
    </Wrap>
  );
}

interface SearchResultsProps {
  isLoading: boolean;
  error: unknown | null;
  results: PlaceSearchResult[];
  onPlaceSelected: (place: PlaceSearchResult) => void;
}

function SearchResults(props: SearchResultsProps) {
  if (props.isLoading) {
    return <Loading />;
  }

  if (props.error) {
    return <Text>Something went wrong</Text>;
  }

  return (
    <>
      {props.results && (
        <SearchResultsWrap>
          {props.results.map((place) => {
            return (
              <SearchResultItem
                onClick={() => {
                  props.onPlaceSelected(place);
                }}
                key={place.placeId}
              >
                <Text margin="0">{place.description}</Text>
              </SearchResultItem>
            );
          })}
        </SearchResultsWrap>
      )}
    </>
  );
}

interface PlaceFormProps {
  placeId: string;
  setSelectedPlaceId: React.Dispatch<SetStateAction<string>>;
  setSearchTerm: React.Dispatch<SetStateAction<string>>;
}

function PlaceForm(props: PlaceFormProps) {
  const { track } = useAnalytics();
  const client = useGqlClient();
  const { data, isLoading, error } = usePlaceQuery(client, {
    id: props.placeId,
  });

  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");

  const [nameError, setNameError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [addressError, setAddressError] = useState("");
  const [excludedHours, setExcludedHours] = useState<ExcludedHoursInput[]>([]);
  const [editingOpeningHours, setEditingOpeningHours] = useState(false);
  const [openingHours, setOpeningHours] = useState<OpeningHoursInput[]>([
    {
      dayOfWeek: "Monday",
      startHour: 12,
      startMinute: 0,
      endHour: 22,
      endMinute: 0,
    },
    {
      dayOfWeek: "Tuesday",
      startHour: 12,
      startMinute: 0,
      endHour: 22,
      endMinute: 0,
    },
    {
      dayOfWeek: "Wednesday",
      startHour: 12,
      startMinute: 0,
      endHour: 22,
      endMinute: 0,
    },
    {
      dayOfWeek: "Thursday",
      startHour: 12,
      startMinute: 0,
      endHour: 22,
      endMinute: 0,
    },
    {
      dayOfWeek: "Friday",
      startHour: 12,
      startMinute: 0,
      endHour: 22,
      endMinute: 0,
    },
    {
      dayOfWeek: "Saturday",
      startHour: 12,
      startMinute: 0,
      endHour: 22,
      endMinute: 0,
    },
    {
      dayOfWeek: "Sunday",
      startHour: 12,
      startMinute: 0,
      endHour: 22,
      endMinute: 0,
    },
  ]);
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);

  const createLocation = useCreateLocationMutation(client);
  const brand = useSelector(authSelectors.activeBrand);
  const account = useSelector(authSelectors.account);
  const domain = account
    ? account.email.substring(account?.email.lastIndexOf("@") + 1)
    : "example.com";

  const history = useHistory();

  useEffect(() => {
    if (!data) {
      return;
    }

    setAddress(data.place.address);
    setPhone(data.place.phone);

    if (data.place.openingHours && data.place.openingHours.length > 0) {
      const ohs: OpeningHoursInput[] = data.place.openingHours.map((oh) => {
        const dow = oh.dayOfWeek;

        // Handle empty or invalid time strings
        if (!oh.openTime || !oh.closeTime) {
          // Default to reasonable business hours if times are missing
          return {
            dayOfWeek: dow,
            startHour: 9,
            startMinute: 0,
            endHour: 17,
            endMinute: 0,
          };
        }

        // Parse start time, defaulting to 9am if invalid
        let startHour: number;
        let startMinute: number;
        try {
          startHour = parseInt(oh.openTime.substring(0, 2), 10);
          startMinute = parseInt(oh.openTime.substring(3, 5), 10);
          if (isNaN(startHour) || startHour < 0 || startHour > 23)
            startHour = 9;
          if (isNaN(startMinute) || startMinute < 0 || startMinute > 59)
            startMinute = 0;
        } catch {
          startHour = 9;
          startMinute = 0;
        }

        // Parse end time, defaulting to 5pm if invalid
        let endHour: number;
        let endMinute: number;
        try {
          endHour = parseInt(oh.closeTime.substring(0, 2), 10);
          endMinute = parseInt(oh.closeTime.substring(3, 5), 10);
          if (isNaN(endHour) || endHour < 0 || endHour > 23) endHour = 17;
          if (isNaN(endMinute) || endMinute < 0 || endMinute > 59)
            endMinute = 0;
        } catch {
          endHour = 17;
          endMinute = 0;
        }

        return {
          dayOfWeek: dow,
          startHour,
          startMinute,
          endHour,
          endMinute,
        };
      });

      setOpeningHours(ohs);
    }
  }, [data]);

  if (isLoading) {
    return <Loading />;
  }

  if (error || !data || !brand) {
    return <Text>Something went wrong</Text>;
  }

  const defaultExcludedHours: ExcludedHoursInput = {
    startHour: openingHours[0].startHour,
    startMinute: openingHours[0].startMinute,
    endHour: openingHours[0].endHour,
    endMinute: openingHours[0].endMinute,
    dayOfWeek: openingHours[0].dayOfWeek,
  };

  return (
    <>
      <Input
        margin="0 0 l 0"
        label="Name"
        value={name}
        autoFocus
        placeholder="eg. Borough"
        error={nameError}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          setNameError("");
          setName(e.currentTarget.value);
        }}
      />
      <TextArea
        margin="0 0 l 0"
        label="Address"
        error={addressError}
        defaultValue={data.place.address}
        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
          setAddressError("");
          setAddress(e.currentTarget.value);
        }}
        rows={3}
      />
      <Input
        margin="0 0 l 0"
        label="Email"
        error={emailError}
        value={email}
        type="email"
        placeholder={`e.g. borough@${domain}`}
        help="Add a location email to let them know about confirmed bookings"
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          setEmailError("");
          setEmail(e.currentTarget.value);
        }}
      />
      <Input
        margin="0 0 l 0"
        label="Phone"
        value={phone}
        type="tel"
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          setPhone(e.currentTarget.value);
        }}
      />

      <Flex margin="l 0 0 0" justify="space-between" align="center">
        <div>
          <Text margin="0" weight="semi">
            Opening Hours
          </Text>
        </div>
        <InlineBtn
          label={editingOpeningHours ? "Done" : "Edit"}
          onClick={() => {
            setEditingOpeningHours(!editingOpeningHours);
          }}
        />
      </Flex>
      {editingOpeningHours ? (
        <>
          {openingHours.map((ehs, i) => {
            return (
              <Hours
                place={data.place}
                index={i}
                key={`${ehs.dayOfWeek}-${ehs.startHour}-${ehs.startMinute}-${ehs.endHour}-${ehs.endMinute}`}
                defaultValue={ehs}
                onChange={(index, ehs) => {
                  const newOpeningHours = [...openingHours];
                  newOpeningHours.splice(index, 1);
                  newOpeningHours.splice(index, 0, ehs);

                  setOpeningHours(newOpeningHours);
                }}
                onRemove={(index) => {
                  const newOpeningHours = [...openingHours];
                  newOpeningHours.splice(index, 1);

                  setOpeningHours(newOpeningHours);
                }}
              />
            );
          })}
          <InlineAddBtn
            label="Add Hours"
            margin="m 0 xl 0"
            onClick={() => {
              const newOpeningHours = [...openingHours];
              newOpeningHours.push({
                dayOfWeek: "Monday",
                startHour: 12,
                startMinute: 0,
                endHour: 22,
                endMinute: 0,
              });
              setOpeningHours(newOpeningHours);
            }}
          />
        </>
      ) : (
        <DetailList
          margin="s 0 xl 0"
          fields={openingHours
            .sort((a, b) => {
              const daysOfWeek = [
                "Monday",
                "Tuesday",
                "Wednesday",
                "Thursday",
                "Friday",
                "Saturday",
                "Sunday",
              ];
              return (
                daysOfWeek.indexOf(a.dayOfWeek) -
                daysOfWeek.indexOf(b.dayOfWeek)
              );
            })
            .map((ohs, i) => ({
              label: ohs.dayOfWeek,
              value: `${ohs.startHour < 10 ? "0" : ""}${ohs.startHour}:${
                ohs.startMinute
              }${ohs.startMinute < 10 ? "0" : ""} - ${
                ohs.endHour < 10 ? "0" : ""
              }${ohs.endHour}:${ohs.endMinute}${ohs.endMinute < 10 ? "0" : ""}`,
            }))}
        />
      )}

      {showAdvancedOptions ? (
        <OptionsToggleWrap onClick={() => setShowAdvancedOptions(false)}>
          <Chevron colorPreset="link" direction="down" />
          <Text weight="semi" colorPreset="link" margin="0 0 0 s">
            Hide advanced options
          </Text>
        </OptionsToggleWrap>
      ) : (
        <OptionsToggleWrap onClick={() => setShowAdvancedOptions(true)}>
          <Chevron colorPreset="link" />
          <Text weight="semi" colorPreset="link" margin="0 0 0 s">
            Show advanced options
          </Text>
        </OptionsToggleWrap>
      )}

      {showAdvancedOptions && (
        <>
          <Text margin="xl 0 0 0" weight="semi">
            Excluded Hours
          </Text>
          <Text size="s" colorPreset="secondary" margin="xs 0 0 0">
            Block off certain hours of the week to prevent influencers applying
          </Text>

          {excludedHours.map((ehs, i) => {
            return (
              <ExcludedHours
                openingHours={openingHours}
                index={i}
                key={`${ehs.dayOfWeek}-${ehs.startHour}-${ehs.startMinute}-${ehs.endHour}-${ehs.endMinute}`}
                defaultValue={ehs}
                onChange={(index, ehs) => {
                  const newExcludedHours = [...excludedHours];
                  newExcludedHours.splice(index, 1);
                  newExcludedHours.splice(index, 0, ehs);

                  setExcludedHours(newExcludedHours);
                }}
                onRemove={(index) => {
                  const newExcludedHours = [...excludedHours];
                  newExcludedHours.splice(index, 1);

                  setExcludedHours(newExcludedHours);
                }}
              />
            );
          })}
          <InlineAddBtn
            label="Add Hours"
            margin="m 0 0"
            onClick={() => {
              const newExcludedHours = [...excludedHours];
              newExcludedHours.push(defaultExcludedHours);
              setExcludedHours(newExcludedHours);
            }}
          />
        </>
      )}

      <CTA
        to="#"
        margin="xl 0 0 0"
        onClick={() => {
          if (!name) {
            setNameError("Name cannot be blank");
            return;
          }

          createLocation.mutate(
            {
              brandId: brand.id,
              placesId: props.placeId,
              name,
              lat: data.place.lat,
              lng: data.place.lng,
              email,
              phone,
              address,
              excludedHours,
              openingHours,
            },
            {
              onSuccess: (res) => {
                toast.success("Location created");
                track("Create location", {
                  id: res.createLocation.location?.id
                    ? res.createLocation.location.id
                    : undefined,
                  brand: brand ? brand.name : undefined,
                });
                history.push("/b/locations");
              },
            }
          );
        }}
      >
        {createLocation.isLoading ? "Loading..." : "Save"}
      </CTA>
    </>
  );
}

function getAvailableHours() {
  const hours = [];

  for (let i = 0; i < 24; i++) {
    hours.push({
      label: `${i < 10 ? "0" : ""}${i}:00`,
      value: {
        hour: i,
        minute: 0,
      },
    });

    hours.push({
      label: `${i < 10 ? "0" : ""}${i}:30`,
      value: {
        hour: i,
        minute: 30,
      },
    });
  }

  return hours;
}

interface HoursProps {
  defaultValue: ExcludedHoursInput | OpeningHoursInput;
  place: Place;
  onChange: (
    index: number,
    ehs: ExcludedHoursInput | OpeningHoursInput
  ) => void;
  onRemove: (index: number) => void;
  index: number;
}

export function Hours(props: HoursProps) {
  const [selectedDay, setSelectedDay] = useState("");
  const [startHour, setStartHour] = useState<number | undefined>();
  const [endHour, setEndHour] = useState<number | undefined>();
  const [startMinute, setStartMinute] = useState<number | undefined>();
  const [endMinute, setEndMinute] = useState<number | undefined>();
  const [startTimeDropdownLabel, setStartTimeDropdownLabel] =
    useState<string>("");
  const [endTimeDropdownLabel, setEndTimeDropdownLabel] = useState<string>("");

  // Helper to format time for display in Dropdown label
  const formatTimeDisplay = (hour?: number, minute?: number): string => {
    if (hour === undefined || minute === undefined) return "Select Time";
    const hrStr = hour < 10 ? `0${hour}` : `${hour}`;
    const minStr = minute === 0 ? "00" : `${minute}`; // minute will be 0 or 30
    return `${hrStr}:${minStr}`;
  };

  const { defaultValue, onChange, index, onRemove } = props;

  // Helper to parse "HH:MM" string label back to hour and minute
  const parseTimeLabel = (
    timeLabel: string
  ): { hour: number; minute: number } => {
    const parts = timeLabel.split(":");
    const hour = parseInt(parts[0], 10);
    const minute = parseInt(parts[1], 10);
    return { hour, minute };
  };

  useEffect(() => {
    setSelectedDay(defaultValue.dayOfWeek);
    setStartHour(defaultValue.startHour);
    setStartMinute(defaultValue.startMinute);
    setEndHour(defaultValue.endHour);
    setEndMinute(defaultValue.endMinute);

    setStartTimeDropdownLabel(
      formatTimeDisplay(defaultValue.startHour, defaultValue.startMinute)
    );
    setEndTimeDropdownLabel(
      formatTimeDisplay(defaultValue.endHour, defaultValue.endMinute)
    );
  }, [defaultValue]);

  useEffect(() => {
    if (
      selectedDay === "" ||
      !startHour ||
      !endHour ||
      startMinute === undefined ||
      endMinute === undefined
    ) {
      return;
    }

    // Only call onChange if end time is after start time
    const startTimeInMinutes = startHour * 60 + startMinute;
    const endTimeInMinutes = endHour * 60 + endMinute;

    if (endTimeInMinutes > startTimeInMinutes) {
      onChange(index, {
        startHour,
        startMinute,
        endHour,
        endMinute,
        dayOfWeek: selectedDay,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- onChange is a parent callback, excluding to avoid re-running on parent re-renders
  }, [selectedDay, startHour, startMinute, endHour, endMinute, index]);

  const weekDays = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
  ];

  const timeOptionsForDropdown = getAvailableHours().map((opt) => ({
    label: opt.label,
    value: opt.label,
  }));

  // Filter end time options to only show times after the selected start time
  const getFilteredEndTimeOptions = () => {
    if (!startHour || startMinute === undefined) {
      return timeOptionsForDropdown;
    }

    const startTimeInMinutes = startHour * 60 + startMinute;
    return timeOptionsForDropdown.filter((opt) => {
      const { hour, minute } = parseTimeLabel(opt.label);
      const optionTimeInMinutes = hour * 60 + minute;
      return optionTimeInMinutes > startTimeInMinutes;
    });
  };

  const filteredEndTimeOptions = getFilteredEndTimeOptions();

  return (
    <Flex align="center" margin="m 0 0 0">
      <DropdownWrap>
        <Dropdown
          size="xs"
          selectedOptions={[selectedDay]}
          setSelectedOptions={(value) => setSelectedDay(value[0])}
          renderLabel={() => {
            const selectedDayString = weekDays.find((e) => e === selectedDay);
            return selectedDayString ? selectedDayString : "None";
          }}
          disableOptionSort
          selectionMode="single"
          options={weekDays.map((day) => ({
            label: day,
            value: day,
          }))}
        />
      </DropdownWrap>

      <DropdownWrap margin="0 0 0 m" size="s">
        <Dropdown
          size="xs"
          selectedOptions={[startTimeDropdownLabel]}
          setSelectedOptions={(value) => {
            const newLabel = value[0];
            setStartTimeDropdownLabel(newLabel);
            const { hour, minute } = parseTimeLabel(newLabel);
            setStartHour(hour);
            setStartMinute(minute);

            // Reset end time if it's now invalid
            if (endHour !== undefined && endMinute !== undefined) {
              const startTimeInMinutes = hour * 60 + minute;
              const endTimeInMinutes = endHour * 60 + endMinute;
              if (endTimeInMinutes <= startTimeInMinutes) {
                setEndHour(undefined);
                setEndMinute(undefined);
                setEndTimeDropdownLabel("Select Time");
              }
            }
          }}
          renderLabel={() => startTimeDropdownLabel || "Select Time"}
          disableOptionSort
          selectionMode="single"
          options={timeOptionsForDropdown}
        />
      </DropdownWrap>

      <Text margin="0 s 0 s">to</Text>

      <DropdownWrap margin="0 m 0 0" size="s">
        <Dropdown
          margin="0 m 0 0"
          size="xs"
          selectedOptions={[endTimeDropdownLabel]}
          setSelectedOptions={(value) => {
            const newLabel = value[0];
            setEndTimeDropdownLabel(newLabel);
            const { hour, minute } = parseTimeLabel(newLabel);
            setEndHour(hour);
            setEndMinute(minute);
          }}
          renderLabel={() => endTimeDropdownLabel || "Select Time"}
          disableOptionSort
          selectionMode="single"
          options={filteredEndTimeOptions}
        />
      </DropdownWrap>

      <div
        onClick={() => {
          onRemove(index);
        }}
        style={{ display: "flex", alignItems: "center", cursor: "pointer" }}
      >
        <CancelIcon />
      </div>
    </Flex>
  );
}

const DropdownWrap = styled(View)<{ size?: "s" }>`
  min-width: ${(p) => (p.size === "s" ? "80px" : "130px")};

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    min-width: ${(p) => (p.size === "s" ? "50px" : "80px")};
  }
`;
